<template>
  <CHeader
    class="p-0 p-lg-3 the-header-vue3 flex-column-reverse"
    :class="{
      'the-header--contact-control-polarity': isContactControlPolarityRoute,
      'the-header--period-schedules': isPeriodSchedulesRoute,
      'the-header--queue-settings': isQueueSettingsRoute,
      'the-header--create-pass': isCreatePassRoute
    }"
    role="banner"
    aria-label="Page Header"
  >
    <CHeaderNav aria-label="Breadcrumb" class="mx-auto c-breadcrumb-container">
      <CNavItem />
    </CHeaderNav>

    <nav
      class="py-2 px-2 px-lg-0 py-md-0 header-actions-container w-100"
      aria-label="Header Actions"
    >
      <ul
        class="header-nav mb-0 list-unstyled d-flex flex-wrap align-items-center"
      >
        <CNavItem class="me-auto">
          <h1
            class="c-header-breadcrumb-title lh-normal"
            data-test-id="header-breadcrumb-title"
          >
            {{ headerBreadcrumbTitle }}
            <HelpCenterButton
              v-if="helpButtonView"
              classes="ms-2"
              :content-key="helpButtonView"
              :aria-label="`Open Help Center for ${headerRouteLabel}`"
            />
          </h1>
        </CNavItem>
        <CNavItem class="me-2 d-flex align-items-center">
          <NetworkConnectivityStatusIcon class="me-2" />
          <LocaleSelector />
        </CNavItem>

        <CNavItem
          v-if="isVisibleActionButtons"
          :class="{
            'header-route-actions--polarity-reflow':
              isContactControlPolarityRoute || isPeriodSchedulesRoute
          }"
        >
          <HeaderActions></HeaderActions>
        </CNavItem>

        <!-- Create | Help | App Switcher | Notifications (App Switcher = 2nd from right) -->
        <CNavItem
          v-if="userRole !== 'student'"
          class="pe-1 c-d-legacy-none d-flex align-items-center flex-nowrap flex-shrink-0 header-create-notif-cluster"
        >
          <TheHeaderCreateButton :user-role="userRole" />
          <HelpCenterButton
            data-test-id="header-helpcenter-btn"
            classes="header-help-btn"
            content-key="header asist_v0002"
            width="28"
            height="28"
          />
          <AppSwitcher />
          <TheHeaderDropdownNotif data-test-id="header-notification-btn" />
        </CNavItem>

        <CNavItem
          v-if="userRole === 'student' && isStudentDigitalIdRouteAllowed"
          class="d-flex align-items-center px-2"
        >
          <TheHeaderDigitalID />
        </CNavItem>

        <CNavItem
          v-if="userRole === 'student'"
          class="d-flex align-items-center px-2 flex-shrink-0"
        >
          <TheHeaderDropdownNotif data-test-id="header-notification-btn" />
        </CNavItem>
        <CNavItem
          v-if="userRole === 'student'"
          class="me-2 me-sm-3 d-flex align-items-center"
          data-test-id="student-header-helpcenter-btn"
        >
          <HelpCenterButton
            width="17"
            height="17"
            content-key="student_header"
          />
        </CNavItem>
      </ul>
    </nav>
  </CHeader>
</template>

<script>
import { isV3ComponentVisible } from "@/helpers/featureFlags.js"
import LocaleSelector from "@/v3components/Forms/LocaleSelector.vue"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import { computed, onMounted, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useStore } from "vuex"
import AppSwitcher from "./AppSwitcher.vue"
import HeaderActions from "./HeaderActions.vue"
import TheHeaderCreateButton from "./TheHeaderCreateButton.vue"
import TheHeaderDigitalID from "./TheHeaderDigitalID.vue"
import TheHeaderDropdownNotif from "./TheHeaderDropdownNotif.vue"
import NetworkConnectivityStatusIcon from "@/v3components/shared/Alerts/NetworkConnectivityStatusIcon.vue"

export default {
  name: "TheHeader",
  components: {
    AppSwitcher,
    TheHeaderDropdownNotif,
    TheHeaderCreateButton,
    HelpCenterButton,
    HeaderActions,
    TheHeaderDigitalID,
    LocaleSelector,
    NetworkConnectivityStatusIcon
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const { t, te } = useI18n()

    const state = reactive({
      routeRequiredModule: null
    })

    const headerBreadcrumbTitle = computed(() => {
      const matched = route.matched || []
      const termRecord = matched.find(
        (r) => r.meta?.behaviourTerminologyHeaderKey
      )
      if (termRecord?.meta?.behaviourTerminologyHeaderKey) {
        const key = termRecord.meta.behaviourTerminologyHeaderKey
        const term = store.getters["behaviourSettings/behaviourTerminology"]
        return term?.[key] || ""
      }
      let i18nKey
      for (let i = matched.length - 1; i >= 0; i -= 1) {
        const key = matched[i]?.meta?.i18n_name_key
        if (key) {
          i18nKey = key
          break
        }
      }
      return i18nKey ? t(i18nKey) : ""
    })

    const user = computed(() => store.getters["authentication/user"])

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isStudentDigitalIdRouteAllowed = computed(
      () => store.getters["routerDependencies/isStudentDigitalIdRouteAllowed"]
    )

    const isVisibleActionButtons = computed(() => {
      return (
        isV3ComponentVisible("HeaderActions") ||
        (state.routeRequiredModule &&
          state.routeRequiredModule.includes("Tardy Management"))
      )
    })

    const helpButtonView = computed(() => {
      const matched = route.matched || []

      for (let i = matched.length - 1; i >= 0; i -= 1) {
        const record = matched[i]
        const behaviourHelpKeys = record.meta?.behaviourManageHelpKeys

        if (behaviourHelpKeys) {
          const roleKey = behaviourHelpKeys[userRole.value]
          if (roleKey) return roleKey
        }

        if (record.meta?.helpButtonView && userRole.value !== "student") {
          return record.meta.helpButtonView
        }
      }

      return false
    })
    const headerRouteLabel = computed(() => {
      const key = route?.meta?.i18n_name_key
      if (key && typeof key === "string" && te(key)) {
        return t(key)
      }
      if (route?.name) {
        return String(route.name)
      }
      return "this page"
    })

    const isContactControlPolarityRoute = computed(
      () => route.name === "Contact Control"
    )

    const isPeriodSchedulesRoute = computed(
      () => route.name === "Period schedules"
    )

    const isQueueSettingsRoute = computed(() => route.name === "Queue Settings")

    const isCreatePassRoute = computed(() => route.name === "Create Pass")

    onMounted(() => {
      if (userRole.value !== "student") {
        store.dispatch("authentication/getUserSettings")
      }
      state.routeRequiredModule = route.meta?.requiredModule
    })

    watch(
      () => route,
      (route) => {
        state.routeRequiredModule = route.meta?.requiredModule
      }
    )

    return {
      route,
      state,
      user,
      userRole,
      isStudentDigitalIdRouteAllowed,
      isVisibleActionButtons,
      helpButtonView,
      headerRouteLabel,
      isContactControlPolarityRoute,
      isPeriodSchedulesRoute,
      isQueueSettingsRoute,
      isCreatePassRoute,
      headerBreadcrumbTitle
    }
  }
}
</script>
